import React, { useState, useRef, useEffect } from "react";
import RadioGroup from "./RadioGroup";

interface Option {
  value: string;
  label: string;
}

interface FilterSelectWithRadioProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  width?: string;
  maxHeight?: string;
  showSearch?: boolean;
  bgColor?: string;
}

const FilterSelectWithRadio: React.FC<FilterSelectWithRadioProps> = ({
  options,
  value,
  onChange,
  id,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  width = "full",
  maxHeight = "250px",
  showSearch = false,
  bgColor = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([
    ...options,
  ]);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedOption = options.find((option) => option.value === value);

  // Determine if search should be shown (either explicitly set or if options > 10)
  const shouldShowSearch = showSearch || options.length >= 5;

  // Filter options based on search term while preserving original order
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredOptions([...options]);
    } else {
      // Filter while preserving the original order
      const searchLower = searchTerm.toLowerCase();
      const filtered = options.filter((option) => {
        return option.label.toLowerCase().includes(searchLower);
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && shouldShowSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset search and filtered options when dropdown closes
    if (!isOpen) {
      setSearchTerm("");
      setFilteredOptions([...options]);
    }
  }, [isOpen, shouldShowSearch, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div>
      <div className="relative" ref={selectRef}>
        <div
          className={`w-full h-[42px] px-3 rounded-[6px] flex items-center justify-between cursor-pointer ${bgColor ? `${bgColor} border border-gray-200` : "bg-[#F9FBFF]"}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span
            className={`text-[#1E1E1E] text-sm ${value ? "" : "text-[#B3B3B3]"
              }`}
            title={selectedOption ? selectedOption.label : placeholder}
            style={{
              maxWidth: "calc(100% - 28px)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
              }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </div>

        {isOpen && (
          <div
            ref={dropdownRef}
            className={`absolute z-50 ${width === "full" ? "w-full" : `${width}`
              } mt-1 bg-white border border-gray-200 rounded-md shadow-lg scrollbar-custom`}
            style={{ maxHeight, overflowY: "auto" }}
          >
            {shouldShowSearch && (
              <div className="sticky top-0 p-2 bg-white border-b border-gray-200 z-10">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-1 flex items-center pointer-events-none">
                    <svg
                      className="h-4 w-4 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="w-full pl-7 pr-3 py-2 rounded-md text-sm focus:outline-none"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            )}
            <RadioGroup
              label=""
              options={filteredOptions}
              value={value}
              onChange={(newValue) => {
                onChange(newValue);
                setIsOpen(false);
              }}
              id={id}
            />
            {filteredOptions.length === 0 && (
              <div className="p-4 text-center text-gray-500 text-sm">
                No options found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSelectWithRadio;
